import { Locale, format, formatRelative, setDefaultOptions } from 'date-fns';

import { VatSchedule } from '@/shared/types/enum';
import { Enum, enumKeys } from '@/shared/utils/enum';
import { invert } from '@/shared/utils/object';

import { enGB } from 'date-fns/locale';

export const DEFAULT_LOCALE = enGB as Locale & { code: 'en-GB' };

setDefaultOptions({ locale: DEFAULT_LOCALE });

export const dateFormat = 'dd/MM/yy';

export const monthFormat = 'MMM';

export const longMonthFormat = 'MMMM';

export const dayMonthFormat = 'd MMM';

export const dayLongMonthFormat = 'd MMMM';

export const dayMonthYearFormat = 'd MMM yy';

export const weekdayMonthYearFormat = 'EEE d MMM yy';

export const timeFormat = 'H:mm';

export const dateTimeFormat = `dd/MM/yy 'at' H:mm`;

export const dayMonthYearTimeFormat = `d MMM yy 'at' H:mm`;

export const exportDateFormat = 'dd/MM/yyyy';

export const exportDateTimeFormat = `dd/MM/yyyy H:mm:ss`;

const parseOrCloneDate = (date: Date | string | null | undefined) =>
  date !== null && date !== undefined
    ? new Date(typeof date === 'string' ? Date.parse(date) : date.getTime())
    : undefined;

export const formatDate = (date: Date | string | null | undefined) => formatDateCustom(date, dateFormat);

export const formatDateTime = (date: Date | string | null | undefined) => formatDateCustom(date, dateTimeFormat);

export const formatMonth = (date: Date | string | null | undefined) => formatDateCustom(date, monthFormat);

export const formatLongMonth = (date: Date | string | null | undefined) => formatDateCustom(date, longMonthFormat);

export const formatDayMonth = (date: Date | string | null | undefined) => formatDateCustom(date, dayMonthFormat);

export const formatDayLongMonth = (date: Date | string | null | undefined) =>
  formatDateCustom(date, dayLongMonthFormat);

export const formatDayMonthYearTime = (date: Date | string | null | undefined) =>
  formatDateCustom(date, dayMonthYearTimeFormat);

export const formatDayMonthYear = (date: Date | string | null | undefined) =>
  formatDateCustom(date, dayMonthYearFormat);

export const formatWeekdayMonthYear = (date: Date | string | null | undefined) =>
  formatDateCustom(date, weekdayMonthYearFormat);

export const formatTime = (date: Date | string | null | undefined) => formatDateCustom(date, timeFormat);

export const formatDateRelative = (date: Date | string | null | undefined) => {
  date = parseOrCloneDate(date);
  return date
    ? upperCaseFirst(formatRelative(date, new Date()))
        // Mild hack to shorten year to 2 digits if present
        .replace(/\/\d{4}$/, (year) => `/${year.slice(-2)}`)
    : '';
};

export const formatDateCustom = (date: Date | string | null | undefined, pattern: string) => {
  date = parseOrCloneDate(date);

  if (!date) {
    return '';
  }

  return date ? format(date, pattern) : '';
};

export const formatEnumKey = (value: string | null | undefined, separator = ' ') =>
  (value ?? '')
    .replace(/([A-Z]{1}|[0-9]+)/g, `${separator}$1`)
    .replaceAll('Aml', 'AML')
    .replaceAll('Cis', 'CIS')
    .replaceAll('Vat', 'VAT')
    .trim();

export const formatEnumKeys = (type: Enum) => enumKeys(type).map((key) => formatEnumKey(key));

export const formatEnum = (type: Enum, value: string | number | null | undefined) => {
  const inverted = invert(type);
  return value !== null && value !== undefined && inverted[value] !== undefined ? formatEnumKey(inverted[value]) : '';
};

export const formatVatSchedule = (schedule: VatSchedule | null | undefined) =>
  formatEnum(VatSchedule, schedule).split(' ').join('/');

export const formatVatScheduleKeys = () => formatEnumKeys(VatSchedule).map((key) => key.split(' ').join('/'));

export const formatClassName = (text: string | null | undefined) =>
  text ? formatEnumKey(text).toLowerCase().replaceAll(' ', '-') : '';

export const formatOrdinal = (value: number | null | undefined) => {
  const ordinalSuffix = (value: number) => {
    if (value > 3 && value < 21) {
      return 'th';
    }

    switch (value % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  return value !== null && value !== undefined ? `${value}${ordinalSuffix(value)}` : '';
};

export const camelize = (text: string) =>
  text.replace(/^([A-Z])|[\s-_]+(\w)/g, (match, p1, p2) => (p2 ? p2.toUpperCase() : p1.toLowerCase()));

export const decamelize = (text: string, separator: string) =>
  text
    .replace(/([a-z\d])([A-Z])/g, `$1${separator}$2`)
    .replace(/([A-Z]+)([A-Z][a-z\d]+)/g, `$1${separator}$2`)
    .toLowerCase();

export const upperCaseFirst = (text: string) => text[0].toUpperCase() + (text.length > 1 ? text.slice(1) : '');

export const formatCompanyNumber = (companyNumber: string | null | undefined) => {
  companyNumber = companyNumber
    ?.trim()
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '');

  return companyNumber && companyNumber.length <= 8 ? companyNumber.padStart(8, '0') : '';
};

export const clip = (text: string | null | undefined, length: number) => {
  if (text) {
    if (text.length > length - 3) {
      return `${text.substring(0, length - 3)}...`;
    }

    return text;
  }

  return '';
};

export const formatList = (items: string[]) =>
  items.map((item, index) => `${index > 0 ? (index < items.length - 1 ? ', ' : ' and ') : ''}${item}`);
